import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingCart, Plus, Minus, Trash2, User, FileText, Percent, DollarSign, X, RefreshCw
} from 'lucide-react';
import { getUnitLabel } from '../../utils/formatStock';

const itemVariants = {
  enter: { opacity: 0, x: 50, height: 0 },
  center: { opacity: 1, x: 0, height: 'auto', transition: { type: 'spring', damping: 25, stiffness: 300 } },
  exit: { opacity: 0, x: -50, height: 0, transition: { duration: 0.2 } },
};

const emptyState = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { type: 'spring', damping: 15, stiffness: 200 } },
};

export default function Cart({
  items = [],
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  discount,
  onDiscountChange,
  taxRate = 0,
  customer,
  onCustomerChange,
  notes,
  onNotesChange,
  onCheckout,
}) {
  const [discountType, setDiscountType] = useState('fixed');
  const [discountInput, setDiscountInput] = useState(discount || '');

  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + (item.salePrice || item.price || 0) * (item.quantity || 1), 0),
    [items]
  );

  const discountValue = discountType === 'percentage'
    ? subtotal * (parseFloat(discountInput) || 0) / 100
    : parseFloat(discountInput) || 0;

  const taxableAmount = subtotal - discountValue;
  const taxAmount = taxableAmount * (taxRate / 100);
  const total = taxableAmount + taxAmount;

  const handleQuantityInput = (item, val) => {
    const qty = parseInt(val, 10);
    if (!isNaN(qty) && qty >= 0) {
      onUpdateQuantity?.(item, qty);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <ShoppingCart size={18} className="text-primary-600 dark:text-primary-400" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Cart</h2>
          <span className="text-xs text-gray-400 dark:text-gray-500">({items.length} items)</span>
        </div>
        {items.length > 0 && (
          <button
            onClick={onClearCart}
            className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors"
          >
            <RefreshCw size={12} />
            Clear
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <AnimatePresence mode="popLayout">
          {items.length === 0 ? (
            <motion.div
              key="empty"
              variants={emptyState}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="flex flex-col items-center justify-center py-16 px-4"
            >
              <div className="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
                <ShoppingCart size={36} className="text-gray-300 dark:text-gray-600" />
              </div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Cart is empty</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Search and add products to start</p>
            </motion.div>
          ) : (
            items.map((item, idx) => (
              <motion.div
                key={item.id || item._id || idx}
                layout
                variants={itemVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/20 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {item.image ? (
                    <img src={item.image} alt={item.productName || item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-lg font-bold text-gray-400">
                      {(item.productName || item.name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {item.productName || item.name}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    Rs. {(item.salePrice || item.price || 0).toLocaleString()} / {getUnitLabel(item.saleUnit)}
                  </p>
                </div>

                <div className="flex items-center gap-1">
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      const qty = (item.quantity || 1) - 1;
                      if (qty <= 0) {
                        onRemoveItem?.(item);
                      } else {
                        onUpdateQuantity?.(item, qty);
                      }
                    }}
                    className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Minus size={12} />
                  </motion.button>
                  <input
                    type="number"
                    min="0"
                    value={item.quantity || 1}
                    onChange={(e) => handleQuantityInput(item, e.target.value)}
                    className="w-10 h-7 text-center text-sm font-medium bg-transparent border-0 text-gray-900 dark:text-white focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => onUpdateQuantity?.(item, (item.quantity || 1) + 1)}
                    className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Plus size={12} />
                  </motion.button>
                </div>

                <div className="text-right min-w-[70px]">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Rs. {((item.salePrice || item.price || 0) * (item.quantity || 1)).toLocaleString()}
                  </p>
                </div>

                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => onRemoveItem?.(item)}
                  className="p-1.5 rounded-lg text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <Trash2 size={14} />
                </motion.button>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700">
        <div className="px-5 py-3 space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                {discountType === 'percentage' ? <Percent size={14} /> : <DollarSign size={14} />}
              </div>
              <input
                type="number"
                value={discountInput}
                onChange={(e) => {
                  setDiscountInput(e.target.value);
                  onDiscountChange?.(e.target.value);
                }}
                placeholder={discountType === 'percentage' ? 'Discount %' : 'Discount amount'}
                className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
              />
            </div>
            <button
              onClick={() => {
                setDiscountType((prev) => (prev === 'fixed' ? 'percentage' : 'fixed'));
                setDiscountInput('');
                onDiscountChange?.('');
              }}
              className={`px-2.5 py-2 text-xs font-medium rounded-lg transition-colors ${
                discountType === 'percentage'
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}
            >
              {discountType === 'percentage' ? '%' : 'Rs.'}
            </button>
          </div>

          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
              <User size={14} />
            </div>
            <input
              type="text"
              value={customer?.name || ''}
              onChange={() => {}}
              onClick={onCustomerChange}
              placeholder="Select customer (optional)"
              className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400 cursor-pointer"
              readOnly
            />
          </div>

          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
              <FileText size={14} />
            </div>
            <input
              type="text"
              value={notes}
              onChange={(e) => onNotesChange?.(e.target.value)}
              placeholder="Order notes..."
              className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
            />
          </div>
        </div>

        <div className="px-5 py-3 space-y-1.5 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
            <span className="text-gray-900 dark:text-white font-medium">Rs. {subtotal.toLocaleString()}</span>
          </div>
          {discountValue > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-red-500 dark:text-red-400">Discount</span>
              <span className="text-red-500 dark:text-red-400">-Rs. {discountValue.toLocaleString()}</span>
            </div>
          )}
          {taxRate > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">Tax ({taxRate}%)</span>
              <span className="text-gray-900 dark:text-white">Rs. {taxAmount.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200 dark:border-gray-700">
            <span className="text-gray-900 dark:text-white">Total</span>
            <span className="text-primary-600 dark:text-primary-400">Rs. {total.toLocaleString()}</span>
          </div>
        </div>

        <div className="px-5 py-4">
          <motion.button
            whileHover={items.length > 0 ? { scale: 1.01 } : {}}
            whileTap={items.length > 0 ? { scale: 0.99 } : {}}
            onClick={onCheckout}
            disabled={items.length === 0}
            className="w-full py-3 text-sm font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed bg-primary-600 hover:bg-primary-700 text-white shadow-sm"
          >
            {items.length === 0 ? 'Add items to checkout' : `Checkout - Rs. ${total.toLocaleString()}`}
          </motion.button>
        </div>
      </div>
    </div>
  );
}
